import React, {useEffect, useRef} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useNavigate, useSearchParams, useLocation} from 'react-router-dom';
import RootState from '../../redux/states/root.state';
import Spinner from "../common/Spinner";
import { AppDispatch } from '../../redux/store';
import { loginUserFromToken, logout, fetchUserData } from '../../redux/actions/user.actions';
import ErrorPage from '../../pages/General/ErrorPage';
import permissionCheck from './permissionCheck';
import { jwtDecode } from 'jwt-decode';

interface ProtectedRouteProps {
    children: React.ReactNode;
}

const SpasRoute: React.FC<ProtectedRouteProps> = ({children}) => {
    const dispatch: AppDispatch= useDispatch();
    const {accessToken, loading, error, data: user, refreshing} = useSelector((state: RootState) => state.user);
    const navigate = useNavigate();
    const location = useLocation();
    const searchParams = useSearchParams()[0];
    const jwt = searchParams.get('session_id');

    const jwtProcessedRef = useRef<string | null>(null);
    const jwtLoginCompletedRef = useRef<boolean>(false);
    const shouldRedirectRef = useRef<boolean>(false);

    // Set shouldRedirect to true if JWT is ever present, and keep it true
    useEffect(() => {
        if (jwt) {
            shouldRedirectRef.current = true;
        }
    }, [jwt]);

    useEffect(() => {
        if (!jwt) return;
        if (jwtProcessedRef.current === jwt) return;
        if (refreshing) return;

        // Parse JWT to get user ID
        let jwtUserId: string | null = null;
        try {
            const decodedJwt = jwtDecode(jwt) as any;
            jwtUserId = decodedJwt.sub;
        } catch (error) {
            console.error('Failed to decode JWT:', error);
            return;
        }

        if (accessToken && user && jwtUserId) {
            // Check if current user matches JWT user (case-insensitive comparison)
            const userIdLower = user.id?.toString().toLowerCase();
            const jwtUserIdLower = jwtUserId.toLowerCase();
            if (userIdLower === jwtUserIdLower) {
                // Same user, skip logout and continue
                jwtProcessedRef.current = jwt;
                return;
            }
            
            // Different user, logout first then login
            jwtProcessedRef.current = jwt;
            jwtLoginCompletedRef.current = false;
            dispatch(logout()).then(() => {
                dispatch(loginUserFromToken(jwt))
                    .then(() => {
                        // Fetch user data after login
                        return dispatch(fetchUserData());
                    })
                    .catch(() => {})
                    .finally(() => {
                        jwtLoginCompletedRef.current = true;
                    });
            });
        } else if (accessToken && !user) {
            // Don't process JWT yet, wait for user data to load
            return;
        } else {
            // No current user, login directly
            jwtProcessedRef.current = jwt;
            jwtLoginCompletedRef.current = false;
            dispatch(loginUserFromToken(jwt))
                .then(() => {
                    // Fetch user data after login
                    return dispatch(fetchUserData());
                })
                .catch(() => {})
                .finally(() => {
                    jwtLoginCompletedRef.current = true;
                });
        }
    }, [jwt, refreshing, accessToken, user, dispatch]);

    useEffect(() => {
        if (!jwt && !accessToken && !refreshing) {
            navigate('/login', { state: { from: location.pathname + location.search }, replace: true });
        }
    }, [jwt, accessToken, refreshing, navigate, location]);

    // Redirect to /home if user has view_dashboard permission
    // This runs for all users (with or without JWT) once user data is loaded
    const redirectCompletedRef = useRef<boolean>(false);
    const lastUserIdRef = useRef<string | number | undefined>(undefined);
    
    useEffect(() => {
        if (!shouldRedirectRef.current) return;
        // Reset redirect flag when user changes (new login)
        if (user?.id !== lastUserIdRef.current) {
            redirectCompletedRef.current = false;
            lastUserIdRef.current = user?.id;
        }

        // Only redirect when:
        // 1. User is authenticated
        // 2. User data is loaded (has permissions)
        // 3. Not currently loading
        // 4. Not on /home already
        // 5. Either no JWT (normal login) or JWT login completed
        const shouldCheckRedirect = accessToken && 
                                     user && 
                                     user.permissions && 
                                     !loading && 
                                     !refreshing &&
                                     location.pathname !== '/home' &&
                                     !redirectCompletedRef.current;

        if (!shouldCheckRedirect) return;

        // If there's a JWT, verify user matches before redirecting
        if (jwt) {
            // Wait for JWT login to complete
            if (!jwtLoginCompletedRef.current) return;
            
            // Verify user ID matches JWT (case-insensitive)
            let jwtUserId: string | null = null;
            try {
                const decodedJwt = jwtDecode(jwt) as any;
                jwtUserId = decodedJwt.sub;
            } catch (error) {
                return;
            }

            if (!jwtUserId) return;
            const userIdLower = user.id?.toString().toLowerCase();
            const jwtUserIdLower = jwtUserId.toLowerCase();
            if (userIdLower !== jwtUserIdLower) {
                // User doesn't match JWT, don't redirect yet
                return;
            }
        }

        // Check if user has view_dashboard permission
        const canAccessDashboard = permissionCheck(user, ['view_dashboard']);
        
        if (canAccessDashboard) {
            redirectCompletedRef.current = true;
            // Remove session_id from URL before redirecting
            const newSearchParams = new URLSearchParams(searchParams);
            newSearchParams.delete('session_id');
            const newSearch = newSearchParams.toString();
            const newPath = newSearch ? `?${newSearch}` : '';
            navigate(`/home${newPath}`, { replace: true });
        }
    }, [jwt, accessToken, user, loading, refreshing, navigate, searchParams, location.pathname]);

    // Check if we're waiting for user data after JWT login
    let expectedUserId: string | null = null;
    if (jwt) {
        try {
            const decodedJwt = jwtDecode(jwt) as any;
            expectedUserId = decodedJwt.sub;
        } catch (error) {
            // JWT decode failed, continue with normal flow
        }
    }
    
    // Case-insensitive user ID comparison
    const hasCorrectUser = !jwt || !expectedUserId || (user && expectedUserId && (() => {
        const userIdLower = user.id?.toString().toLowerCase();
        const expectedUserIdLower = expectedUserId.toLowerCase();
        return userIdLower === expectedUserIdLower;
    })());

    return (error && !loading && !refreshing && (!jwt || jwtLoginCompletedRef.current))
        ? <ErrorPage error={error}/>
        : (accessToken && user && hasCorrectUser)
            ? <>{children}</>
            : <Spinner />

};

export default SpasRoute;